import type { Maybe, ValueEvent } from '@adornis/base/utilTypes.js';
import { getAllEntities } from '@adornis/baseql/operations/mongo.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { css } from '@adornis/chemistry/directives/css.js';
import { FormField } from '@adornis/formfield/form-field.js';
import '@adornis/popover/x-dropdown-multiselection.js';
import '@adornis/popover/x-dropdown-selection.js';
import type { XDropdownSelection } from '@adornis/popover/x-dropdown-selection.js';
import { GLOBAL_CONTEXT } from '@adornis/users/db/a-roles.js';
import { AdornisRoleToContextsWrapper } from '@adornis/users/db/roleToContextsWrapper.js';
import { Location } from '@ils/locations/db/location.js';
import { ExamType, prettyUserRole, UserRole } from '@ils/static/enums.js';
import { html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { repeat } from 'lit/directives/repeat.js';

const contextSelection = (
  element: HTMLElement,
  role: { name: UserRole; contexts: string[] },
  locations: Maybe<Array<{ _id: string; displayName: Maybe<string> }>>,
  valuePicked: () => void,
) => {
  if (!needsContext(role.name)) return html` <div></div> `;
  if (role.name === UserRole.EXAMCENTER || role.name === UserRole.EXAMCENTERMANAGER) {
    if (!locations) return nothing;
    return html`
      <x-dropdown-multiselection
        .selectables=${locations}
        .value=${role.contexts.map(id => locations.find(loc => loc._id === id)).filter(Boolean)}
        .renderString=${(loc?: { _id: string; displayName: Maybe<string> }) => loc?.displayName ?? loc?._id ?? ''}
        @value-picked=${(e: ValueEvent<Array<{ _id: string; displayName: Maybe<string> }>>) => {
          role.contexts = e.detail.value.map(loc => loc._id);
          valuePicked();
        }}
      ></x-dropdown-multiselection>
    `;
  }
  if (role.name === UserRole.INSPECTOR) {
    const selectables = Object.values(ExamType);
    return html`
      <x-dropdown-multiselection
        .selectables=${selectables}
        .value=${role.contexts}
        .renderString=${(type?: ExamType) => type ?? ''}
        @value-picked=${(e: ValueEvent<ExamType[]>) => {
          role.contexts = e.detail.value;
          valuePicked();
        }}
      ></x-dropdown-multiselection>
    `;
  }
};

@customElement('ils-role-picker')
export class ILSRolePicker extends FormField<Array<{ name: UserRole; contexts: string[] }>> {
  @state()
  private readonly _locations = new RXController(
    this,
    getAllEntities(Location)({
      _id: 1,
      displayName: 1,
      address: {
        name: 1,
        streetAndHousenumber: 1,
        zipCity: { zip: 1, city: 1 },
        country: 1,
        bundesland: 1,
        additionalInfo: 1,
      },
    }),
  );

  private readonly _dispatchEvent = () => {
    if (!this.value.value) return;
    this.dispatchEvent(
      new CustomEvent('value-picked', {
        detail: {
          value: this.value.value,
        },
      }),
    );
  };

  override render() {
    return html`
      <x-flex space="sm">
        <x-dropdown-selection
          error=${ifDefined(this.error)}
          .selectables=${Object.values(UserRole).filter(r => !this.value.value?.find(v => v.name === r))}
          .renderString=${(role?: UserRole) => (role ? prettyUserRole(role) : '')}
          placeholder="Rolle hinzufügen"
          @value-picked=${(e: ValueEvent<UserRole>) => {
            if (!this.value.value) return;
            const target = e.currentTarget as XDropdownSelection<any>;
            const contexts = needsContext(e.detail.value) ? [] : [GLOBAL_CONTEXT];
            this.value.next([
              ...this.value.value,
              new AdornisRoleToContextsWrapper({ name: e.detail.value, contexts }),
            ]);
            this._dispatchEvent();
            target.value.next(null);
          }}
        ></x-dropdown-selection>
        <x-grid columns="1fr 1fr max-content" space="sm" ${css({ alignItems: 'center' })}>
          ${repeat(
            this.value.value ?? [],
            (role, index) => html`
              <x-text> ${prettyUserRole(role.name)} </x-text>
              ${contextSelection(this, role, this._locations.value, this._dispatchEvent)}
              <x-button
                mode="icon"
                icon="delete"
                tone="error"
                @click=${() => {
                  if (!this.value.value) return;
                  this.value.value.splice(index, 1);
                  this.requestUpdate();
                  this._dispatchEvent();
                }}
              ></x-button>
            `,
          )}
        </x-grid>
      </x-flex>
    `;
  }
}
function needsContext(role: UserRole) {
  return [UserRole.EXAMCENTER, UserRole.EXAMCENTERMANAGER, UserRole.INSPECTOR].includes(role);
}
